import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { addWeeks, addMonths, addYears } from 'date-fns';
import { toDateInputValue } from '../utils/date';
import { supabase } from '../lib/supabase';
import { classifyGoalMovement, resolveGoalCategoryContext } from '../utils/goalContribution';
import { calculateMonthlyTotals } from '../utils/transactionMetrics';

// ===== CATEGORIAS =====
export interface Category {
  id: string;
  name: string;
  icon: string;
  color: string;
  parent_id: string | null;
  is_active: boolean;
  created_at: string;
}

export interface CategoryWithSubs extends Category {
  subcategories: Category[];
}

// ===== TRANSAÇÕES =====
export type TransactionRecurrence = 'none' | 'weekly' | 'biweekly' | 'monthly' | 'annual';
export type TransactionStatus =
  | 'scheduled'
  | 'confirmed'
  | 'paid'
  | 'overdue'
  | 'cancelled'
  | 'active'; // "active" mantido por compatibilidade com dados legados

export interface Transaction {
  id: string;
  description: string;
  amount: number;
  category: 'Moradia' | 'Mercado' | 'Outros' | 'Entrada principal' | 'Financeiro' | 'Freelance' | 'Aporte da Meta'; // Legacy support mantém Freelance
  type: 'income' | 'expense';
  date: string;
  status?: TransactionStatus;
  cancellationReason?: string;
  category_id?: string | null; // Nova referência para tabela categories
  notes?: string | null; // Observações (obrigatório para "Outros")
  customCategoryName?: string | null; // Categoria personalizada informada pelo usuário
  customSubcategoryName?: string | null; // Subcategoria personalizada informada pelo usuário
  recurrenceType?: TransactionRecurrence; // Recorrência selecionada para o lançamento
  recurrenceCount?: number | null; // Quantidade de repetições (quando aplicável)
  recurrenceEndDate?: string | null; // Data final da recorrência
  nextOccurrenceDate?: string | null; // Próxima ocorrência gerada automaticamente
  confirmationDate?: string | null; // Data em que o pagamento foi confirmado
  isForecast?: boolean; // Indica se o lançamento é previsto/futuro
  recurrenceMetadata?: Record<string, unknown> | null; // Metadados adicionais de recorrência (intervalo customizado etc.)
}

export interface Goal {
  id: string;
  name: string;
  targetAmount: number;
  currentAmount: number;
  deadline: string;
  recurring: boolean;
}

// ===== ANALYTICS DE METAS =====
export type GoalHealthStatus = 'excellent' | 'good' | 'attention' | 'critical';
export type ContributionFrequency = 'none' | 'daily' | 'weekly' | 'monthly' | 'sporadic';

export interface GoalAnalytics {
  id: string;
  goal_id: string;
  user_id: string;
  
  // Temporal
  days_remaining: number;
  days_total: number;
  days_elapsed: number;
  
  // Financeiro
  amount_remaining: number;
  monthly_contribution_needed: number;
  daily_contribution_needed: number;
  current_monthly_pace: number;
  
  // Progresso
  progress_percentage: number;
  time_progress_percentage: number;
  projected_completion_date: string | null;
  will_complete_on_time: boolean;
  
  // Histórico
  last_contribution_date: string | null;
  last_contribution_amount: number | null;
  total_contributions: number;
  contribution_frequency: ContributionFrequency;
  
  // Saúde
  status_health: GoalHealthStatus;
  health_score: number;
  needs_attention: boolean;
  alert_message: string | null;
  recommended_action: string | null;
  
  // Meta
  calculated_at: string;
  updated_at: string;
}

export interface GoalWithAnalytics extends Goal {
  analytics?: GoalAnalytics;
}

// ===== GAMIFICATION =====
export type AchievementTier = 'bronze' | 'silver' | 'gold' | 'platinum' | 'diamond';
export type AchievementCategory = 'goals' | 'savings' | 'streaks' | 'budget' | 'special';
export type StreakType = 'contribution' | 'budget' | 'savings' | 'login';

export interface Achievement {
  id: string;
  code: string;
  name: string;
  description: string;
  icon: string;
  category: AchievementCategory;
  tier: AchievementTier;
  points: number;
  requirement_value: number | null;
  requirement_type: string | null;
  is_secret: boolean;
  created_at: string;
}

export interface UserAchievement {
  id: string;
  user_id: string;
  achievement_id: string;
  unlocked_at: string;
  progress_value: number;
  achievement?: Achievement; // Populated via join
}

export interface UserStreak {
  id: string;
  user_id: string;
  streak_type: StreakType;
  current_streak: number;
  longest_streak: number;
  last_activity_date: string | null;
  started_at: string;
  updated_at: string;
}

export interface UserLevel {
  id: string;
  user_id: string;
  current_level: number;
  total_xp: number;
  xp_for_next_level: number;
  total_achievements: number;
  created_at: string;
  updated_at: string;
}

export interface RewardHistory {
  id: string;
  user_id: string;
  reward_type: 'achievement' | 'streak_bonus' | 'level_up';
  reward_title: string;
  xp_gained: number;
  created_at: string;
}

export interface GamificationSummary {
  user_id: string;
  current_level: number;
  total_xp: number;
  xp_for_next_level: number;
  total_achievements: number;
  contribution_streak: number;
  longest_contribution_streak: number;
  unlocked_achievements_count: number;
  total_achievements_count: number;
  recent_achievements: Array<{
    code: string;
    name: string;
    icon: string;
    tier: AchievementTier;
    unlocked_at: string;
  }> | null;
}

// ===== EVENTS =====
export interface Event {
  id: string;
  date: string;
  title: string;
  description: string;
  type: 'payment' | 'goal' | 'reminder';
  amount?: number | null;
  time: string;
  status?: 'scheduled' | 'confirmed' | 'completed' | 'cancelled' | 'overdue';
  source?: 'manual' | 'transaction' | 'goal' | 'system';
  transactionId?: string | null;
  metadata?: Record<string, unknown> | null;
  transactionType?: 'income' | 'expense';
  isVirtual?: boolean;
  baseEventId?: string;
}

export interface FinanceState {
  balance: number;
  monthlyIncome: number;
  monthlyExpenses: number;
  transactions: Transaction[];
  goals: Goal[];
  events: Event[];
  categories: Category[]; // Todas as categorias (principais + subcategorias)
  categoriesWithSubs: CategoryWithSubs[]; // Categorias principais com subcategorias aninhadas
  goalAnalytics: GoalAnalytics[]; // Analytics de todas as metas
  isLoading: boolean;
  error: string | null;
  currentUserId: string | null;
  
  addTransaction: (transaction: Omit<Transaction, 'id'>) => Promise<void>;
  updateTransaction: (id: string, transaction: Partial<Transaction>) => Promise<void>;
  deleteTransaction: (id: string) => Promise<void>;
  addGoal: (goal: Omit<Goal, 'id'>) => Promise<void>;
  updateGoal: (id: string, goal: Partial<Goal>) => Promise<void>;
  deleteGoal: (id: string) => Promise<void>;
  addEvent: (event: Omit<Event, 'id'>) => Promise<void>;
  updateEvent: (id: string, event: Partial<Event>) => Promise<void>;
  deleteEvent: (id: string) => Promise<void>;
  
  syncWithSupabase: () => Promise<void>;
  loadTransactions: () => Promise<void>;
  loadGoals: () => Promise<void>;
  loadEvents: () => Promise<void>;
  loadCategories: () => Promise<void>;
  loadGoalAnalytics: () => Promise<void>; // Nova função
  calculateGoalAnalytics: (goalId: string) => Promise<void>; // Recalcular analytics de uma meta
  calculateMonthlyStats: () => void;
  clearStore: () => void;
  setupRealtimeSubscriptions: () => void;
  cleanupSubscriptions: () => void;
  setCurrentUserId: (userId: string | null) => void;
}

const mockTransactions: Transaction[] = [
  { id: '1', description: 'Compra no Mercado', amount: -130, category: 'Mercado', type: 'expense', date: '2025-10-10' },
  { id: '2', description: 'Entrada principal', amount: 500, category: 'Entrada principal', type: 'income', date: '2025-10-10' },
  { id: '3', description: 'Aluguel', amount: -800, category: 'Moradia', type: 'expense', date: '2025-10-05' },
  { id: '4', description: 'Salário', amount: 3000, category: 'Entrada principal', type: 'income', date: '2025-10-01' }
];

const mockGoals: Goal[] = [
  { id: '1', name: 'TV 65 pol', targetAmount: 3500, currentAmount: 650, deadline: '2025-10-29', recurring: false },
  { id: '2', name: 'PC Gamer', targetAmount: 7500, currentAmount: 50, deadline: '2025-10-29', recurring: false }
];

const mockEvents: Event[] = [
  {
    id: '1',
    date: '2025-10-13',
    title: 'Pagamento Moradia',
    description: 'Aluguel mensal',
    type: 'payment',
    amount: 800,
    time: '09:00'
  },
  {
    id: '2',
    date: '2025-10-15',
    title: 'Meta TV 65pol',
    description: 'Milestone da meta',
    type: 'goal',
    amount: null,
    time: '10:00'
  },
  {
    id: '3',
    date: '2025-10-20',
    title: 'Freelance',
    description: 'Recebimento do projeto',
    type: 'payment',
    amount: 500,
    time: '14:00'
  },
  {
    id: '4',
    date: '2025-10-25',
    title: 'Compra Mercado',
    description: 'Compras mensais',
    type: 'payment',
    amount: 150,
    time: '16:00'
  }
];

const initialState = {
  balance: 0,
  monthlyIncome: 0,
  monthlyExpenses: 0,
  transactions: [],
  goals: [],
  events: [],
  categories: [],
  categoriesWithSubs: [],
  goalAnalytics: [],
  isLoading: false,
  error: null,
  currentUserId: null,
};

let subscriptions: any[] = [];

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Relaxed detection to allow local Supabase instances (localhost / 127.0.0.1)
// Original check required 'supabase.co' in the URL which prevented local dev
// from using the real Supabase integration. For local development we just
// require both URL and anon key to be present.
const isSupabaseConfigured = Boolean(SUPABASE_URL && SUPABASE_ANON_KEY);

function genId() {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

const MAX_VIRTUAL_OCCURRENCES = 12;

type RecurrenceKind = TransactionRecurrence | undefined | null;

interface RecurrenceBlueprint {
  type: RecurrenceKind;
  count?: number | null;
  endDate?: string | null;
}

function computeNextOccurrenceDate(
  baseDate: Date,
  recurrenceType: TransactionRecurrence,
  iteration: number
): Date {
  switch (recurrenceType) {
    case 'weekly':
      return addWeeks(baseDate, iteration);
    case 'biweekly':
      return addWeeks(baseDate, iteration * 2);
    case 'monthly':
      return addMonths(baseDate, iteration);
    case 'annual':
      return addYears(baseDate, iteration);
    default:
      return baseDate;
  }
}

function expandRecurringEvents(events: Event[], transactions: Transaction[] = []): Event[] {
  const expanded: Event[] = [];
  const existingKeys = new Set<string>();
  const transactionMap = new Map(transactions.map((txn) => [txn.id, txn]));

  for (const rawEvent of events) {
    const relatedTransaction = rawEvent.transactionId ? transactionMap.get(rawEvent.transactionId) : undefined;
    const resolvedTransactionType = rawEvent.transactionType
      ?? (rawEvent.metadata?.transaction_type as ('income' | 'expense') | undefined)
      ?? relatedTransaction?.type;

    const event: Event = {
      ...rawEvent,
      transactionType: resolvedTransactionType,
    };

    expanded.push(event);
    existingKeys.add(`${event.transactionId ?? event.id}|${event.date}`);

    const blueprint: RecurrenceBlueprint = {
      type: (event.metadata?.recurrence_type as RecurrenceKind)
        ?? relatedTransaction?.recurrenceType
        ?? 'none',
      count: (event.metadata?.recurrence_count as number | null | undefined)
        ?? relatedTransaction?.recurrenceCount
        ?? null,
      endDate: (event.metadata?.recurrence_end_date as string | null | undefined)
        ?? relatedTransaction?.recurrenceEndDate
        ?? null,
    };

    if (!blueprint.type || blueprint.type === 'none') {
      continue;
    }

    const baseDate = new Date(event.date);
    if (Number.isNaN(baseDate.getTime())) {
      continue;
    }

    const maxOccurrences = Math.min(
      blueprint.count && blueprint.count > 0 ? blueprint.count : MAX_VIRTUAL_OCCURRENCES,
      MAX_VIRTUAL_OCCURRENCES
    );

    const limitByYear = addYears(baseDate, 1);
    const recurrenceEndDate = blueprint.endDate ? new Date(blueprint.endDate) : null;

    for (let occurrence = 1; occurrence < maxOccurrences; occurrence += 1) {
      const candidateDate = computeNextOccurrenceDate(baseDate, blueprint.type as TransactionRecurrence, occurrence);

      if (Number.isNaN(candidateDate.getTime())) {
        break;
      }

      if (candidateDate > limitByYear) {
        break;
      }

      if (recurrenceEndDate && candidateDate > recurrenceEndDate) {
        break;
      }

      const candidateKey = `${event.transactionId ?? event.id}|${toDateInputValue(candidateDate)}`;
      if (existingKeys.has(candidateKey)) {
        continue;
      }

      expanded.push({
        ...event,
        id: `${event.id}-rec-${occurrence}`,
        date: toDateInputValue(candidateDate),
        status: event.status === 'completed' ? event.status : 'scheduled',
        isVirtual: true,
        baseEventId: event.id,
      });
      existingKeys.add(candidateKey);
    }
  }

  return expanded.sort((a, b) => a.date.localeCompare(b.date));
}

export const useFinanceStore = create<FinanceState>()(
  persist(
    (set, get) => ({
      ...initialState,
      
      setCurrentUserId: (userId: string | null) => {
        const currentUserId = get().currentUserId;
        
        if (currentUserId && userId && currentUserId !== userId) {
          console.log('Mudança de usuário detectada, limpando dados do usuário anterior...');
          get().cleanupSubscriptions();
          set({
            ...initialState,
            currentUserId: userId,
          });
        } else {
          set({ currentUserId: userId });
        }
      },
      
      clearStore: () => {
        get().cleanupSubscriptions();
        set(initialState);
      },

      setupRealtimeSubscriptions: () => {
        if (!isSupabaseConfigured) return;

        const setupSubscriptions = async () => {
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) return;

          get().cleanupSubscriptions();

          const transactionsSubscription = supabase
            .channel(`transactions-${user.id}`)
            .on('postgres_changes', 
              { event: '*', schema: 'public', table: 'transactions', filter: `user_id=eq.${user.id}` },
              () => get().loadTransactions()
            )
            .subscribe();

          const goalsSubscription = supabase
            .channel(`goals-${user.id}`)
            .on('postgres_changes', 
              { event: '*', schema: 'public', table: 'goals', filter: `user_id=eq.${user.id}` },
              () => get().loadGoals()
            )
            .subscribe();

          const eventsSubscription = supabase
            .channel(`events-${user.id}`)
            .on('postgres_changes', 
              { event: '*', schema: 'public', table: 'events', filter: `user_id=eq.${user.id}` },
              () => get().loadEvents()
            )
            .subscribe();

          subscriptions = [transactionsSubscription, goalsSubscription, eventsSubscription];
        };

        setupSubscriptions();
      },

      cleanupSubscriptions: () => {
        subscriptions.forEach(subscription => {
          supabase.removeChannel(subscription);
        });
        subscriptions = [];
      },

      addTransaction: async (transaction) => {
        set({ isLoading: true, error: null });
        try {
          if (!isSupabaseConfigured) {
            const newTx: Transaction = { id: genId(), ...transaction };
            set((state) => ({
              transactions: [newTx, ...state.transactions],
              balance: state.balance + transaction.amount,
            }));
            get().calculateMonthlyStats();
            // Criar evento local para manter agenda sincronizada
            const eventTitle = transaction.amount >= 0
              ? `Receita: ${transaction.description}`
              : `Despesa: ${transaction.description}`;
            const newEvent = {
              id: genId(),
              date: transaction.date,
              title: eventTitle,
              description: transaction.notes ?? '',
              type: 'payment' as const,
              amount: Math.abs(transaction.amount),
              time: '09:00',
              status: transaction.isForecast ? 'scheduled' : 'confirmed',
              source: 'transaction' as const,
              transactionId: newTx.id,
              transactionType: transaction.type,
              metadata: {
                recurrence_type: transaction.recurrenceType ?? 'none',
                recurrence_count: transaction.recurrenceCount ?? null,
                recurrence_end_date: transaction.recurrenceEndDate ?? null,
                custom_category: transaction.customCategoryName ?? null,
                custom_subcategory: transaction.customSubcategoryName ?? null,
                is_forecast: transaction.isForecast ?? false,
                transaction_type: transaction.type,
              },
            };
            set((state) => ({ events: [newEvent, ...state.events] }));
            return;
          }

          const { data: { user } } = await supabase.auth.getUser();
          if (!user) throw new Error("Usuário não autenticado");

          const detectedStatus: TransactionStatus = transaction.status
            ? transaction.status
            : transaction.isForecast
              ? 'scheduled'
              : 'confirmed';

          const basePayload: Record<string, any> = {
            description: transaction.description,
            amount: transaction.amount,
            category: transaction.category,
            type: transaction.type,
            date: transaction.date,
            category_id: transaction.category_id ?? null,
            notes: transaction.notes ?? null,
            status: detectedStatus,
            cancellation_reason: transaction.cancellationReason ?? null,
            recurrence_type: transaction.recurrenceType ?? 'none',
            recurrence_count: transaction.recurrenceCount ?? null,
            recurrence_end_date: transaction.recurrenceEndDate ?? null,
            next_occurrence_date: transaction.nextOccurrenceDate ?? null,
            custom_category_name: transaction.customCategoryName ?? null,
            custom_subcategory_name: transaction.customSubcategoryName ?? null,
            confirmation_date: transaction.confirmationDate ?? (detectedStatus === 'paid' ? new Date().toISOString() : null),
            is_forecast: transaction.isForecast ?? detectedStatus === 'scheduled',
            recurrence_metadata: transaction.recurrenceMetadata ?? null,
          };

          await supabase
            .from('transactions')
            .insert({
              ...basePayload,
              user_id: user.id,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            });

          await get().loadTransactions();
          await get().loadEvents();
          get().calculateMonthlyStats();
        } catch (error: any) {
          set({ error: error.message || 'Falha ao adicionar transação.' });
        } finally {
          set({ isLoading: false });
        }
      },
      
      updateTransaction: async (id, updatedTransaction) => {
        set({ isLoading: true, error: null });
        try {
          if (!isSupabaseConfigured) {
            set((state) => {
              const prev = state.transactions.find(t => t.id === id);
              const transactions = state.transactions.map((t) => 
                t.id === id ? { ...t, ...updatedTransaction } : t
              );
              let balance = state.balance;
              if (prev && updatedTransaction.amount !== undefined) {
                balance = state.balance - prev.amount + updatedTransaction.amount;
              }
              return { transactions, balance };
            });
            get().calculateMonthlyStats();
            return;
          }

          const { data: { user } } = await supabase.auth.getUser();
          if (!user) throw new Error("Usuário não autenticado");

          const updatePayload: Record<string, any> = {
            updated_at: new Date().toISOString(),
          };

          if (updatedTransaction.description !== undefined) updatePayload.description = updatedTransaction.description;
          if (updatedTransaction.amount !== undefined) updatePayload.amount = updatedTransaction.amount;
          if (updatedTransaction.category !== undefined) updatePayload.category = updatedTransaction.category;
          if (updatedTransaction.type !== undefined) updatePayload.type = updatedTransaction.type;
          if (updatedTransaction.date !== undefined) updatePayload.date = updatedTransaction.date;
          if (updatedTransaction.category_id !== undefined) updatePayload.category_id = updatedTransaction.category_id;
          if (updatedTransaction.notes !== undefined) updatePayload.notes = updatedTransaction.notes;
          if (updatedTransaction.status !== undefined) updatePayload.status = updatedTransaction.status;
          if (updatedTransaction.cancellationReason !== undefined) updatePayload.cancellation_reason = updatedTransaction.cancellationReason;
          if (updatedTransaction.recurrenceType !== undefined) updatePayload.recurrence_type = updatedTransaction.recurrenceType;
          if (updatedTransaction.recurrenceCount !== undefined) updatePayload.recurrence_count = updatedTransaction.recurrenceCount;
          if (updatedTransaction.recurrenceEndDate !== undefined) updatePayload.recurrence_end_date = updatedTransaction.recurrenceEndDate;
          if (updatedTransaction.nextOccurrenceDate !== undefined) updatePayload.next_occurrence_date = updatedTransaction.nextOccurrenceDate;
          if (updatedTransaction.customCategoryName !== undefined) updatePayload.custom_category_name = updatedTransaction.customCategoryName;
          if (updatedTransaction.customSubcategoryName !== undefined) updatePayload.custom_subcategory_name = updatedTransaction.customSubcategoryName;
          if (updatedTransaction.confirmationDate !== undefined) updatePayload.confirmation_date = updatedTransaction.confirmationDate;
          if (updatedTransaction.isForecast !== undefined) updatePayload.is_forecast = updatedTransaction.isForecast;
          if (updatedTransaction.recurrenceMetadata !== undefined) updatePayload.recurrence_metadata = updatedTransaction.recurrenceMetadata;

          await supabase
            .from('transactions')
            .update(updatePayload)
            .eq('id', id)
            .eq('user_id', user.id);

          await get().loadTransactions();
          await get().loadEvents();
          get().calculateMonthlyStats();
        } catch (error: any) {
          set({ error: error.message || 'Falha ao atualizar transação.' });
        } finally {
          set({ isLoading: false });
        }
      },
      
      deleteTransaction: async (id) => {
        set({ isLoading: true, error: null });
        try {
          if (!isSupabaseConfigured) {
            set((state) => {
              const txn = state.transactions.find(t => t.id === id);
              return {
                transactions: state.transactions.filter((t) => t.id !== id),
                balance: txn ? state.balance - txn.amount : state.balance,
              };
            });
            get().calculateMonthlyStats();
            return;
          }
          
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) throw new Error("Usuário não autenticado");

          await supabase
            .from('transactions')
            .delete()
            .eq('id', id)
            .eq('user_id', user.id);

          get().calculateMonthlyStats();
        } catch (error: any) {
          set({ error: error.message || 'Falha ao deletar transação.' });
        } finally {
          set({ isLoading: false });
        }
      },
      
      addGoal: async (goal) => {
        set({ isLoading: true, error: null });
        try {
          if (!isSupabaseConfigured) {
            set((state) => ({ goals: [{ id: genId(), ...goal }, ...state.goals] }));
            return;
          }

          const { data: { user } } = await supabase.auth.getUser();
          if (!user) throw new Error("Usuário não autenticado");

          const { data: newGoal, error } = await supabase
            .from('goals')
            .insert({
              user_id: user.id,
              name: goal.name,
              target_amount: goal.targetAmount,
              current_amount: goal.currentAmount,
              deadline: goal.deadline,
              recurring: goal.recurring,
            })
            .select()
            .single();

          if (error) throw error;

          await get().loadGoals();

          if (newGoal) {
            await get().addEvent({
              date: newGoal.deadline,
              title: `Prazo final: ${newGoal.name}`,
              description: `Data limite para a meta "${newGoal.name}".`,
              type: 'goal',
              time: '09:00'
            });
          }
        } catch (error: any) {
          set({ error: error.message || 'Falha ao adicionar meta.' });
        } finally {
          set({ isLoading: false });
        }
      },
      
      updateGoal: async (id, updatedGoal) => {
        set({ isLoading: true, error: null });
        try {
          if (!isSupabaseConfigured) {
            set((state) => ({
              goals: state.goals.map((g) => 
                g.id === id ? { ...g, ...updatedGoal } : g
              ),
            }));
            window.dispatchEvent(new CustomEvent('goalUpdated', { 
              detail: { success: true, message: 'Meta atualizada com sucesso!' } 
            }));
            return;
          }

          const { data: { user } } = await supabase.auth.getUser();
          if (!user) throw new Error("Usuário não autenticado");

          const updateData: any = { updated_at: new Date().toISOString() };

          if (updatedGoal.name !== undefined) updateData.name = updatedGoal.name;
          if (updatedGoal.targetAmount !== undefined) updateData.target_amount = updatedGoal.targetAmount;
          if (updatedGoal.currentAmount !== undefined) updateData.current_amount = updatedGoal.currentAmount;
          if (updatedGoal.deadline !== undefined) updateData.deadline = updatedGoal.deadline;
          if (updatedGoal.recurring !== undefined) updateData.recurring = updatedGoal.recurring;

          const { error } = await supabase
            .from('goals')
            .update(updateData)
            .eq('id', id)
            .eq('user_id', user.id);

          if (error) throw error;

          set({ isLoading: false, error: null });
          await get().loadGoals();

          window.dispatchEvent(new CustomEvent('goalUpdated', { 
            detail: { success: true, message: 'Meta atualizada com sucesso! ✅' } 
          }));

        } catch (error: any) {
          const errorMsg = error.message || 'Falha ao atualizar meta.';
          set({ isLoading: false, error: errorMsg });
          
          window.dispatchEvent(new CustomEvent('goalUpdated', { 
            detail: { success: false, message: `Erro: ${errorMsg}` } 
          }));
          throw error;
        }
      },
      
      deleteGoal: async (id) => {
        set({ isLoading: true, error: null });
        try {
          if (!isSupabaseConfigured) {
            set((state) => ({ goals: state.goals.filter((g) => g.id !== id) }));
            window.dispatchEvent(new CustomEvent('goalUpdated', { 
              detail: { success: true, message: 'Meta cancelada com sucesso!' } 
            }));
            return;
          }

          const { data: { user } } = await supabase.auth.getUser();
          if (!user) throw new Error("Usuário não autenticado");

          const { error } = await supabase
            .from('goals')
            .delete()
            .eq('id', id)
            .eq('user_id', user.id);

          if (error) throw error;

          set({ isLoading: false, error: null });
          await get().loadGoals();

          window.dispatchEvent(new CustomEvent('goalUpdated', { 
            detail: { success: true, message: 'Meta cancelada com sucesso! 🗑️' } 
          }));

        } catch (error: any) {
          const errorMsg = error.message || 'Falha ao deletar meta.';
          set({ isLoading: false, error: errorMsg });
          
          window.dispatchEvent(new CustomEvent('goalUpdated', { 
            detail: { success: false, message: `Erro ao cancelar: ${errorMsg}` } 
          }));
          throw error;
        }
      },

      addEvent: async (event) => {
        set({ isLoading: true, error: null });
        try {
          if (!isSupabaseConfigured) {
            set((state) => ({ events: [{ id: genId(), ...event }, ...state.events] }));
            return;
          }

          const { data: { user } } = await supabase.auth.getUser();
          if (!user) throw new Error("Usuário não autenticado");

          await supabase
            .from('events')
            .insert({ ...event, user_id: user.id });

          await get().loadEvents();
        } catch (error: any) {
          set({ error: error.message || 'Falha ao adicionar evento.' });
        } finally {
          set({ isLoading: false });
        }
      },

      updateEvent: async (id, updatedEvent) => {
        set({ isLoading: true, error: null });
        try {
          if (!isSupabaseConfigured) {
            set((state) => ({
              events: state.events.map((e) => e.id === id ? { ...e, ...updatedEvent } : e),
            }));
            return;
          }

          const { data: { user } } = await supabase.auth.getUser();
          if (!user) throw new Error("Usuário não autenticado");

          await supabase
            .from('events')
            .update({ ...updatedEvent, updated_at: new Date().toISOString() })
            .eq('id', id)
            .eq('user_id', user.id);
        } catch (error: any) {
          set({ error: error.message || 'Falha ao atualizar evento.' });
        } finally {
          set({ isLoading: false });
        }
      },

      deleteEvent: async (id) => {
        set({ isLoading: true, error: null });
        try {
          if (!isSupabaseConfigured) {
            set((state) => ({ events: state.events.filter((e) => e.id !== id) }));
            return;
          }

          const { data: { user } } = await supabase.auth.getUser();
          if (!user) throw new Error("Usuário não autenticado");

          await supabase
            .from('events')
            .delete()
            .eq('id', id)
            .eq('user_id', user.id);
        } catch (error: any) {
          set({ error: error.message || 'Falha ao deletar evento.' });
        } finally {
          set({ isLoading: false });
        }
      },

      loadTransactions: async () => {
        set({ isLoading: true, error: null });
        try {
          if (!isSupabaseConfigured) {
            const transactions = get().transactions.length ? get().transactions : mockTransactions;
            const balance = transactions.reduce((sum, t) => sum + t.amount, 0);
            set({ transactions, balance, isLoading: false });
            get().calculateMonthlyStats();
            return;
          }

          const { data: { user } } = await supabase.auth.getUser();
          if (!user) {
            set({ isLoading: false });
            return;
          }

          const { data, error } = await supabase
            .from('transactions')
            .select('*')
            .eq('user_id', user.id)
            .order('date', { ascending: false });

          if (error) throw error;

          const transactions: Transaction[] = data.map((t: Record<string, any>) => ({
            id: t.id,
            description: t.description,
            amount: typeof t.amount === 'number' ? t.amount : parseFloat(t.amount),
            category: t.category,
            type: t.type,
            date: t.date,
            status: (t.status ?? 'active') as TransactionStatus,
            cancellationReason: t.cancellation_reason ?? null,
            category_id: t.category_id ?? null,
            notes: t.notes ?? null,
            customCategoryName: t.custom_category_name ?? null,
            customSubcategoryName: t.custom_subcategory_name ?? null,
            recurrenceType: (t.recurrence_type ?? 'none') as TransactionRecurrence,
            recurrenceCount: t.recurrence_count ?? null,
            recurrenceEndDate: t.recurrence_end_date ?? null,
            nextOccurrenceDate: t.next_occurrence_date ?? null,
            confirmationDate: t.confirmation_date ?? null,
            isForecast: t.is_forecast ?? false,
          }));

          const categoriesWithSubs = get().categoriesWithSubs || [];
          const { goalCategory, goalCategoryIds } = resolveGoalCategoryContext(categoriesWithSubs);

          const normalizedTransactions = transactions.map((transaction) => {
            const movementKind = classifyGoalMovement(transaction, goalCategoryIds);

            if (!movementKind) {
              return transaction;
            }

            const targetCategoryId = goalCategory?.id ?? transaction.category_id ?? null;

            return {
              ...transaction,
              category: 'Aporte da Meta',
              category_id: targetCategoryId,
            };
          });

          const balance = normalizedTransactions.reduce((sum, t) => sum + t.amount, 0);
          set({ transactions: normalizedTransactions, balance, isLoading: false });
          get().calculateMonthlyStats();
        } catch (error: any) {
          set({ isLoading: false, error: error.message || 'Erro ao carregar transações.' });
        }
      },

      loadGoals: async () => {
        set({ isLoading: true, error: null });
        try {
          if (!isSupabaseConfigured) {
            const goals = get().goals.length ? get().goals : mockGoals;
            set({ goals, isLoading: false });
            return;
          }

          const { data: { user } } = await supabase.auth.getUser();
          if (!user) {
            set({ isLoading: false });
            return;
          }

          const { data, error } = await supabase
            .from('goals')
            .select('*')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false });

          if (error) throw error;

          const goals: Goal[] = data.map(g => ({
            id: g.id,
            name: g.name,
            targetAmount: parseFloat(g.target_amount),
            currentAmount: parseFloat(g.current_amount),
            deadline: g.deadline,
            recurring: g.recurring
          }));

          set({ goals, isLoading: false });
        } catch (error: any) {
          set({ isLoading: false, error: error.message || 'Erro ao carregar metas.' });
        }
      },

      loadEvents: async () => {
        set({ isLoading: true, error: null });
        try {
          if (!isSupabaseConfigured) {
            const state = get();
            const rawEvents = state.events.length
              ? state.events.filter(event => !event.isVirtual)
              : mockEvents;
            set({ events: expandRecurringEvents(rawEvents, state.transactions), isLoading: false });
            return;
          }

          const { data: { user } } = await supabase.auth.getUser();
          if (!user) {
            set({ isLoading: false });
            return;
          }

          const { data, error } = await supabase
            .from('events')
            .select('*')
            .eq('user_id', user.id)
            .order('date', { ascending: false });

          if (error) throw error;

          const supabaseEvents: Event[] = data.map(e => ({
            id: e.id,
            date: e.date,
            title: e.title,
            description: e.description,
            type: e.type,
            amount: e.amount,
            time: e.time,
            status: e.status,
            source: e.source,
            transactionId: e.transaction_id,
            metadata: e.metadata ?? null,
            transactionType: (e.metadata?.transaction_type as 'income' | 'expense' | undefined) ?? undefined,
          }));

          const events = expandRecurringEvents(supabaseEvents, get().transactions);

          set({ events, isLoading: false });
        } catch (error: any) {
          set({ isLoading: false, error: error.message || 'Erro ao carregar eventos.' });
        }
      },

      // ===== CARREGAR CATEGORIAS =====
      loadCategories: async () => {
        set({ isLoading: true, error: null });
        try {
          if (!isSupabaseConfigured) {
            set({ isLoading: false, categories: [], categoriesWithSubs: [] });
            return;
          }

          const { data, error } = await supabase
            .from('categories')
            .select('*')
            .eq('is_active', true)
            .order('name', { ascending: true });

          if (error) throw error;

          const categories: Category[] = data || [];

          // Separar categorias principais (parent_id = null)
          const mainCategories = categories.filter(c => c.parent_id === null);

          // Organizar hierarquicamente
          const categoriesWithSubs: CategoryWithSubs[] = mainCategories.map(main => ({
            ...main,
            subcategories: categories.filter(c => c.parent_id === main.id)
          }));

          set({ categories, categoriesWithSubs, isLoading: false });
        } catch (error: any) {
          set({ isLoading: false, error: error.message || 'Erro ao carregar categorias.' });
        }
      },

      // ===== CARREGAR ANALYTICS DAS METAS =====
      loadGoalAnalytics: async () => {
        set({ isLoading: true, error: null });
        try {
          if (!isSupabaseConfigured) {
            set({ isLoading: false, goalAnalytics: [] });
            return;
          }

          const { data: { user } } = await supabase.auth.getUser();
          if (!user) {
            set({ isLoading: false });
            return;
          }

          const { data, error } = await supabase
            .from('goal_analytics')
            .select('*')
            .eq('user_id', user.id)
            .order('needs_attention', { ascending: false })
            .order('health_score', { ascending: true });

          if (error) throw error;

          const goalAnalytics: GoalAnalytics[] = data || [];

          set({ goalAnalytics, isLoading: false });
        } catch (error: any) {
          set({ isLoading: false, error: error.message || 'Erro ao carregar analytics das metas.' });
        }
      },

      // ===== CALCULAR ANALYTICS DE UMA META ESPECÍFICA =====
      calculateGoalAnalytics: async (goalId: string) => {
        try {
          if (!isSupabaseConfigured) return;

          const { data: { user } } = await supabase.auth.getUser();
          if (!user) return;

          // Chamar função SQL que calcula analytics
          const { error } = await supabase.rpc('calculate_goal_analytics', {
            p_goal_id: goalId,
            p_user_id: user.id
          });

          if (error) throw error;

          // Recarregar analytics
          await get().loadGoalAnalytics();
        } catch (error: any) {
          console.error('Erro ao calcular analytics da meta:', error.message);
        }
      },

      calculateMonthlyStats: () => {
        const { transactions } = get();
        const referenceDate = new Date();

        const { income: monthlyIncome, expenses: monthlyExpenses } = calculateMonthlyTotals(
          transactions,
          referenceDate
        );

        set({ monthlyIncome, monthlyExpenses });
      },

      syncWithSupabase: async () => {
        const { loadTransactions, loadGoals, loadEvents, loadCategories, loadGoalAnalytics, setupRealtimeSubscriptions, setCurrentUserId } = get();
        
        if (isSupabaseConfigured) {
          const { data: { user } } = await supabase.auth.getUser();
          setCurrentUserId(user?.id || null);
        } else {
          setCurrentUserId('demo-user');
        }
        
        await loadTransactions();
        await loadGoals();
        await loadEvents();
        await loadCategories(); // Carregar categorias do Supabase
        await loadGoalAnalytics(); // Carregar analytics das metas
        setupRealtimeSubscriptions();
      }
    }),
    {
      name: 'finance-storage',
      partialize: (state) => ({
        transactions: state.transactions,
        goals: state.goals,
        events: state.events,
        categories: state.categories,
        categoriesWithSubs: state.categoriesWithSubs,
        balance: state.balance,
        monthlyIncome: state.monthlyIncome,
        monthlyExpenses: state.monthlyExpenses,
        currentUserId: state.currentUserId,
      }),
      merge: (persistedState: any, currentState) => {
        const storedUserId = persistedState?.currentUserId;
        const currentUserId = currentState.currentUserId;
        
        if (storedUserId && currentUserId && storedUserId !== currentUserId) {
          return currentState;
        }
        
        return { ...currentState, ...persistedState };
      },
    }
  )
);